import React from "react";

const safeGet = (obj, path, defaultValue = "") => {
  try {
    const result = path.split('.').reduce((o, key) => (o && o[key] !== undefined && o[key] !== null) ? o[key] : null, obj);
    return (result !== undefined && result !== null) ? result : defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime()) ? date.toLocaleDateString('en-IN') : "";
  } catch (e) {
    return "";
  }
};

export default function DesignPrintSummary({ job, onClose }) {
  if (!job) return null;

  // For print button
  const handlePrint = () => window.print();

  // For product row, you may need to adapt this if your job has multiple products
  const products = [
    {
      productCode: job.productCode,
      size: job.productSize || "",
      quantity: job.originalQuantity,
    }
  ];

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="mb-4 print:hidden">
        <button 
          onClick={handlePrint}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Print Form
        </button>
        {onClose && (
          <button 
            onClick={onClose}
            className="ml-2 bg-gray-400 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
          >
            Close
          </button>
        )}
      </div>
      <div className="border border-black print:m-0 print:w-full print:h-full" style={{ pageBreakAfter: 'always' }}>
        {/* Header */}
        <div className="flex border-b border-black">
          <div className="w-1/2 border-r border-black p-1 text-center flex flex-col justify-center">
            <div className="text-xs">
              <p className="font-bold">From,</p>
              <p className="font-bold">Printverse Pvt.Ltd.</p>
              <p>20 Jura Tukoganj, Opp. Ramlagh Petrol Pump.</p>
              <p>Indore (M.P.) - 452005</p>
              <p>Contact - 7389825004, 7038231237</p>
            </div>
          </div>
          <div className="w-1/2 p-1 text-center flex flex-col justify-center">
            <div className="text-xs">
              <p className="font-bold">To,</p>
              <p className="font-bold">{safeGet(job, 'orderId.leadId.name')}</p>
              <p>{safeGet(job, 'orderId.flat')}{safeGet(job, 'orderId.flat') && safeGet(job, 'orderId.area') ? ", " : ""}{safeGet(job, 'orderId.area')}</p>
              <p>
                {safeGet(job, 'orderId.city')}{safeGet(job, 'orderId.city') && safeGet(job, 'orderId.pincode') ? ", " : ""}{safeGet(job, 'orderId.pincode')}
              </p>
              <p>Contact - {safeGet(job, 'orderId.primaryPhone')}</p>
            </div>
          </div>
        </div>
        {/* Job Order Title Row */}
        <div className="flex border-b border-black">
          <div className="w-3/4 p-1 border-r border-black">
            <p className="text-lg font-bold">Job Order Proforma (Job ID: {safeGet(job, '_id', '').slice(-6)})</p>
          </div>
          <div className="w-1/4 p-1 flex items-center">
            <span className="mr-1 text-xs">Priority:</span>
            <div className="text-xs">{safeGet(job, 'priority')}</div>
          </div>
        </div>
        {/* Order Details Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Order No.:</strong> {safeGet(job, 'orderId._id', '')}</p>
          </div>
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Order Date:</strong> {formatDate(safeGet(job, 'orderId.createdAt'))}</p>
          </div>
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Dispatch Date:</strong> {formatDate(safeGet(job, 'orderId.dispatchDate'))}</p>
          </div>
          <div className="w-1/4 p-1">
            <p><strong>Order By:</strong> {safeGet(job, 'orderId.leadId.name')}</p>
          </div>
        </div>
        {/* Customer Name Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Customer Name:</strong> {safeGet(job, 'orderId.leadId.name')}</p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Product:</strong> {safeGet(job, 'productCode')}</p>
          </div>
        </div>
        {/* Products Table */}
        <div className="border-b border-black">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-black">
                <th className="border-r border-black p-1 w-8 text-center">Sr No.</th>
                <th className="border-r border-black p-1 w-1/5">Design</th>
                <th className="border-r border-black p-1 w-1/5">Input Q.</th>
                <th className="border-r border-black p-1 w-1/5">Final Dam.</th>
                <th className="p-1 w-1/5">Final Q.</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={index} className={index < products.length - 1 ? "border-b border-black" : ""}>
                  <td className="border-r border-black p-1 text-center">{index + 1}.</td>
                  <td className="border-r border-black p-1">
                    <p><strong>Code:</strong> {safeGet(product, 'productCode')}</p>
                    <p><strong>Size:</strong> {safeGet(product, 'size')}</p>
                    <p><strong>Q:</strong> {safeGet(product, 'quantity')}</p>
                  </td>
                  <td className="border-r border-black p-1">{safeGet(job, 'design.outputQuantity')}</td>
                  <td className="border-r border-black p-1">{safeGet(job, 'production.damageQuantity')}</td>
                  <td className="p-1">{safeGet(job, 'dispatch.finalQuantity')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Print File and Cut File Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Print File:</strong> {safeGet(job, 'design.printingFileName')}</p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Cut File:</strong> {safeGet(job, 'design.cuttingFileName')}</p>
          </div>
        </div>
        {/* Dispatch Mode and Packed By Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Disp. Mode:</strong> {safeGet(job, 'dispatch.dispatchMode')}</p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Packed by:</strong></p>
          </div>
        </div>
        {/* Weight/Box and Package Date Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Wgt/Box:</strong> {safeGet(job, 'dispatch.weight')}</p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Pkg Date:</strong></p>
          </div>
        </div>
        {/* Special Instructions Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Special Instructions</strong></p>
          </div>
          <div className="w-3/4">
            <table className="w-full border-collapse">
              <tbody>
                {[1, 2, 3].map((num) => (
                  <tr key={num} className={num < 3 ? "border-b border-black" : ""}>
                    <td className="border-r border-black p-1 w-8 text-center">{num}.</td>
                    <td className="p-1">{num === 1 ? safeGet(job, 'design.instructions') : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Department tracking table */}
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black p-1 w-1/6">Department</th>
              <th className="border-r border-black p-1 w-1/6">Person</th>
              <th className="border-r border-black p-1 w-1/6">Start Date & Time</th>
              <th className="border-r border-black p-1 w-1/6">End Date & Time</th>
              <th className="border-r border-black p-1 w-1/6">Input/Damage Q.</th>
              <th className="p-1 w-1/6">Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-black">
              <td className="border-r border-black p-1 font-bold">Design</td>
              <td className="border-r border-black p-1">{safeGet(job, 'design.designer')}</td>
              <td className="border-r border-black p-1">{formatDate(safeGet(job, 'design.designStartDate'))}</td>
              <td className="border-r border-black p-1">{formatDate(safeGet(job, 'design.designCompletedAt'))}</td>
              <td className="border-r border-black p-1">{safeGet(job, 'design.outputQuantity')}</td>
              <td className="p-1">{safeGet(job, 'design.comments')}</td>
            </tr>
            <tr className="border-b border-black">
              <td className="border-r border-black p-1 font-bold">Machine</td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="p-1"></td>
            </tr>
            <tr className="border-b border-black">
              <td className="border-r border-black p-1 font-bold">Production</td>
              <td className="border-r border-black p-1">{safeGet(job, 'production.operator')}</td>
              <td className="border-r border-black p-1">{formatDate(safeGet(job, 'production.productionStartDate'))}</td>
              <td className="border-r border-black p-1">{formatDate(safeGet(job, 'production.productionCompletedAt'))}</td>
              <td className="border-r border-black p-1">{safeGet(job, 'production.outputQuantity')}</td>
              <td className="p-1">{safeGet(job, 'production.comments')}</td>
            </tr>
            <tr>
              <td className="border-r border-black p-1 font-bold">Packing</td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="p-1"></td>
            </tr>
          </tbody>
        </table>
      </div>
      <style jsx global>{`
        @page {
          size: A4;
          margin: 0;
        }
        @media print {
          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:w-full {
            width: 100%;
          }
          .print\\:h-full {
            height: 100%;
          }
          .print\\:m-0 {
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
} 